/**
 * Map registry.
 *
 * ── The invariant this file exists to protect ─────────────────────────────
 *
 * The client predicts movement by running the SAME `stepWorld` the server runs,
 * against the SAME geometry. If this `MapDef` differs from the server's by one
 * platform, prediction diverges on that platform every single tick and the
 * player rubber-bands on a ledge that "looks fine".
 *
 * So the map is never authored here twice. `mapId` arrives in `world_init` and
 * resolves to a definition that both runtimes import. Until the shared map
 * package lands (maps are authored in v1's arena editor and exported), the
 * graybox bowl from `@chaos/sim` stands in for every id and `known()` reports
 * false so the caller can say so out loud rather than silently mispredicting.
 */

import { ARENA_IDS, createArena, DEFAULT_ARENA_RADIUS } from '@chaos/sim';

/**
 * The five launch arenas from GAME-DESIGN §7. Theme only — the geometry comes
 * from the shared definition once the map pipeline exists.
 *
 * RADIUS IS NOT AUTHORED HERE. It used to be, one number per map (45, 42, 40,
 * 46, 44), and every one of them was fiction: all six ids resolve to the same
 * graybox from `@chaos/sim`, so the radius written here was the ONLY thing that
 * made them differ — and it differed by overriding the shared map's own size.
 *
 * That is how the offline gym ended up building a 45-unit bowl after the arena
 * was resized to 25 for the fixed camera: `LocalConnection` reads its arena
 * radius straight from this table. The one place the game gets playtested was
 * the one place still on the old geometry, and it looked exactly like a big
 * empty arena because that is what it was.
 *
 * Per-map radii come back when per-map GEOMETRY does, and then they arrive
 * together, from the map definition, as one fact.
 */
export const MAP_META = {
  // Names are the theme; the ids are not. STOCK-THEME 6.7 re-themes the five
  // arenas, and the ids stay because a `mapId` is written into every replay
  // header and the seed commitment — the same reason `characterId` did not move
  // when the roster became tickers.
  crucible: { name: 'The Pit', theme: 'pit', sky: 'sunset', particles: 'embers' },
  spire: { name: 'The Chart', theme: 'terminal', sky: 'void', particles: 'dust' },
  grid: { name: 'The Exchange', theme: 'exchange', sky: 'storm', particles: 'dust' },
  drift: { name: 'Bubble', theme: 'bubble', sky: 'aurora', particles: 'magic' },
  foundry: { name: 'HFT Floor', theme: 'hft', sky: 'void', particles: 'ash' },
  flat: { name: 'Graybox Bowl', theme: 'stone', sky: 'starfield', particles: 'dust' },
};

const _cache = new Map();

/**
 * Resolves a map for rendering AND prediction.
 *
 * @param {string} mapId
 * @param {{ arenaRadius?: number, playerCount?: number }} opts values from
 *   `world_init`, so the standin at least matches the arena the zone was
 *   scheduled against.
 */
export function getMapDef(mapId, opts = {}) {
  const key = `${mapId}|${opts.arenaRadius ?? ''}|${opts.playerCount ?? ''}`;
  const cached = _cache.get(key);
  if (cached) return cached;

  const meta = MAP_META[mapId] ?? MAP_META.flat;
  // `createArena` is the ONE place an id becomes geometry, and the server calls
  // the same function. Building the client's copy separately is exactly the
  // divergence this file's header warns about — it only ever looked safe while
  // every id resolved to the same single map.
  const map = createArena(mapId, {
    radius: opts.arenaRadius ?? DEFAULT_ARENA_RADIUS,
    playerCount: opts.playerCount ?? 8,
  });
  map.name = meta.name;
  _cache.set(key, map);
  return map;
}

/**
 * True once a map's real geometry is available rather than the graybox.
 *
 * The list is the SIM'S — `ARENA_IDS` — because "which arenas are real" is
 * decided by whichever ids `createArena` actually builds geometry for, and a
 * second copy here would answer the question wrongly the moment a sixth arena
 * lands. This returned `'flat'` only, so every one of the five real arenas
 * logged "no shared definition yet; predicting against the graybox bowl" —
 * a warning that stopped being true when they moved into the sim.
 */
export function known(mapId) {
  return mapId === 'flat' || ARENA_IDS.includes(mapId);
}

export function getMapMeta(mapId) {
  return MAP_META[mapId] ?? MAP_META.flat;
}
